import {
    ApiResponse,
    AuthResponse,
    CreateReviewRequest,
    Image,
    PaginatedResponse,
    Review,
    ReviewListParams,
    ReviewListResponse,
    ScanResponse,
    ScannedWine,
    ScannedWinesResponse,
    TelegramAuthRequest,
    User,
    UserStats,
    Wine,
    WineListParams,
    WineListResponse,
    Winemaker,
} from '@/types'

export interface RequestConfig {
    headers?: Record<string, string>
    timeout?: number
    signal?: AbortSignal
}

export interface ApiError {
    code: number
    message: string
}

export class ApiService {
    private baseURL: string
    private defaultTimeout: number = 5000
    private onUnauthorized?: () => void
    private onRefreshToken?: () => Promise<boolean> // Callback для refresh токена
    private lastRequestId?: string

    constructor(baseURL: string = import.meta.env?.VITE_API_BASE_URL || '/api/v1') {
        this.baseURL = baseURL
    }

    public setOnUnauthorized(handler: () => void) {
        this.onUnauthorized = handler
    }

    public setOnRefreshToken(handler: () => Promise<boolean>) {
        this.onRefreshToken = handler
    }

    private getAuthHeaders(): Record<string, string> {
        const token = localStorage.getItem('access_token')
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        }

        if (token) {
            headers.Authorization = `Bearer ${token}`
        }

        return headers
    }

    private attachRequestId(headers: Record<string, string>) {
        if (this.lastRequestId) {
            headers['X-Request-Id'] = this.lastRequestId
        }
    }

    private captureRequestId(response: Response) {
        const header = response.headers?.get('X-Request-Id')
        if (header?.trim()) {
            this.lastRequestId = header.trim()
        }
    }

    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {},
        config?: RequestConfig
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseURL}${endpoint}`
        console.log(`🌐 API Request: ${options.method || 'GET'} ${url}`)
        console.log(`📤 Request body:`, options.body)

        // Don't add Content-Type header for FormData
        const isFormData = options.body instanceof FormData
        const baseHeaders = isFormData
            ? {
                  ...(localStorage.getItem('access_token')
                      ? { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
                      : {}),
              }
            : this.getAuthHeaders()

        const buildHeaders = (base: Record<string, string>) => {
            const headers = {
                ...base,
                ...config?.headers,
                ...options.headers,
            }
            this.attachRequestId(headers)
            return headers
        }

        const headers = buildHeaders(baseHeaders)
        console.log(`🔧 Request headers:`, headers)
        if (isFormData) {
            console.log(`📋 FormData detected - Content-Type will be set automatically by browser`)
        }

        // Setup timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(
            () => controller.abort(),
            config?.timeout || this.defaultTimeout
        )

        try {
            const response = await fetch(url, {
                ...options,
                headers,
                signal: config?.signal || controller.signal,
            })

            this.captureRequestId(response)

            clearTimeout(timeoutId)

            console.log(`📥 API Response: ${response.status} ${response.statusText}`)
            const isRefreshEndpoint = endpoint === '/auth/refresh'

            if (!response.ok) {
                if (response.status === 401 && !isRefreshEndpoint) {
                    const refreshSuccess = await this.handleTokenRefresh()
                    if (refreshSuccess) {
                        // используем уже вычисленный выше isFormData
                        const retryBaseHeaders = isFormData
                            ? {
                                  ...(localStorage.getItem('access_token')
                                      ? {
                                            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                                        }
                                      : {}),
                              }
                            : this.getAuthHeaders()
                        // объединяем через buildHeaders, чтобы подтянуть config/options и X-Request-Id
                        const retryHeaders = buildHeaders(retryBaseHeaders)

                        const retryResponse = await fetch(url, {
                            ...options,
                            headers: retryHeaders,
                        })

                        this.captureRequestId(retryResponse)

                        if (retryResponse.ok) {
                            const data = await retryResponse.json()
                            return { success: true, data }
                        } else if (retryResponse.status === 401) {
                            this.onUnauthorized?.()
                        }
                    } else {
                        this.onUnauthorized?.()
                    }
                }

                // Handle HTTP errors with RFC7807 mapping when available
                const errorData = await response.json().catch(() => ({}))
                return {
                    success: false,
                    error: {
                        code: response.status,
                        message:
                            errorData.detail ||
                            errorData.title ||
                            errorData.error?.message ||
                            errorData.message ||
                            `HTTP ${response.status}`,
                    },
                }
            }

            const data = await response.json()
            return {
                success: true,
                data: data,
            }
        } catch (error) {
            clearTimeout(timeoutId)

            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    return {
                        success: false,
                        error: {
                            code: 0,
                            message: 'Request timeout',
                        },
                    }
                }

                return {
                    success: false,
                    error: {
                        code: -1,
                        message: error.message || 'Network error',
                    },
                }
            }

            return {
                success: false,
                error: {
                    code: -1,
                    message: 'Unknown error occurred',
                },
            }
        }
    }

    private async handleTokenRefresh(): Promise<boolean> {
        // Используем callback от AuthService для единой точки refresh
        return this.onRefreshToken ? await this.onRefreshToken() : false
    }

    // Generic methods
    async get<T>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(url, { method: 'GET' }, config)
    }

    async post<T>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(
            url,
            {
                method: 'POST',
                body: data ? JSON.stringify(data) : undefined,
            },
            config
        )
    }

    async put<T>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(
            url,
            {
                method: 'PUT',
                body: data ? JSON.stringify(data) : undefined,
            },
            config
        )
    }

    async delete<T>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(url, { method: 'DELETE' }, config)
    }

    async upload<T>(
        url: string,
        formData: FormData,
        config?: RequestConfig
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(
            url,
            {
                method: 'POST',
                body: formData,
            },
            config
        )
    }

    // Auth methods
    async authTelegramUser(telegramUser: TelegramAuthRequest): Promise<ApiResponse<AuthResponse>> {
        return this.post<AuthResponse>('/auth/telegram', telegramUser)
    }

    async authLogin(username: string, password: string): Promise<ApiResponse<AuthResponse>> {
        return this.post<AuthResponse>('/auth/login', { username, password })
    }

    async refreshToken(refreshToken: string): Promise<ApiResponse<AuthResponse>> {
        return this.post<AuthResponse>('/auth/refresh', { refresh: refreshToken })
    }

    async logout(): Promise<ApiResponse<void>> {
        return this.post<void>('/auth/logout')
    }

    // Wine methods
    async getWines(params?: WineListParams): Promise<ApiResponse<WineListResponse>> {
        const queryString = params
            ? new URLSearchParams(
                  Object.entries(params).reduce(
                      (acc, [key, value]) => {
                          if (value !== undefined && value !== null) {
                              acc[key] = String(value)
                          }
                          return acc
                      },
                      {} as Record<string, string>
                  )
              ).toString()
            : ''

        return this.get<WineListResponse>(`/wines${queryString ? `?${queryString}` : ''}`)
    }

    async getWine(id: number): Promise<ApiResponse<Wine>> {
        return this.get<Wine>(`/wines/${id}`)
    }

    async getWineReviews(
        id: number,
        params?: ReviewListParams
    ): Promise<ApiResponse<ReviewListResponse>> {
        const queryString = params
            ? new URLSearchParams(
                  Object.entries(params).reduce(
                      (acc, [key, value]) => {
                          if (value !== undefined && value !== null) {
                              acc[key] = String(value)
                          }
                          return acc
                      },
                      {} as Record<string, string>
                  )
              ).toString()
            : ''

        return this.get<ReviewListResponse>(
            `/wines/${id}/reviews${queryString ? `?${queryString}` : ''}`
        )
    }

    // Review methods
    async getReviews(params?: ReviewListParams): Promise<ApiResponse<ReviewListResponse>> {
        const queryString = params
            ? new URLSearchParams(
                  Object.entries(params).reduce(
                      (acc, [key, value]) => {
                          if (value !== undefined && value !== null) {
                              acc[key] = String(value)
                          }
                          return acc
                      },
                      {} as Record<string, string>
                  )
              ).toString()
            : ''

        return this.get<ReviewListResponse>(`/reviews${queryString ? `?${queryString}` : ''}`)
    }

    async getReview(id: number): Promise<ApiResponse<Review>> {
        return this.get<Review>(`/reviews/${id}`)
    }

    async createReview(data: CreateReviewRequest): Promise<ApiResponse<Review>> {
        return this.post<Review>('/reviews', data)
    }

    async updateReview(
        id: number,
        data: Partial<CreateReviewRequest>
    ): Promise<ApiResponse<Review>> {
        return this.put<Review>(`/reviews/${id}`, data)
    }

    async deleteReview(id: number): Promise<ApiResponse<void>> {
        return this.delete<void>(`/reviews/${id}`)
    }

    // User methods
    async getMe(): Promise<ApiResponse<User>> {
        return this.get<User>('/users/me')
    }

    async getMyScans(params?: {
        page?: number
        limit?: number
    }): Promise<ApiResponse<PaginatedResponse<Wine>>> {
        const queryString = params
            ? new URLSearchParams(
                  Object.entries(params).reduce(
                      (acc, [key, value]) => {
                          if (value !== undefined && value !== null) {
                              acc[key] = String(value)
                          }
                          return acc
                      },
                      {} as Record<string, string>
                  )
              ).toString()
            : ''

        return this.get<PaginatedResponse<Wine>>(
            `/users/me/scans${queryString ? `?${queryString}` : ''}`
        )
    }

    async getMyReviews(params?: ReviewListParams): Promise<ApiResponse<ReviewListResponse>> {
        const queryString = params
            ? new URLSearchParams(
                  Object.entries(params).reduce(
                      (acc, [key, value]) => {
                          if (value !== undefined && value !== null) {
                              acc[key] = String(value)
                          }
                          return acc
                      },
                      {} as Record<string, string>
                  )
              ).toString()
            : ''

        return this.get<ReviewListResponse>(
            `/users/me/reviews${queryString ? `?${queryString}` : ''}`
        )
    }

    async updateMe(data: Partial<User>): Promise<ApiResponse<User>> {
        return this.put<User>('/users/me', data)
    }

    async getMyStats(): Promise<ApiResponse<UserStats>> {
        return this.get<UserStats>('/users/me/stats')
    }

    // Winemaker methods
    async getWinemakers(params?: {
        page?: number
        limit?: number
    }): Promise<ApiResponse<PaginatedResponse<Winemaker>>> {
        const queryString = params
            ? new URLSearchParams(
                  Object.entries(params).reduce(
                      (acc, [key, value]) => {
                          if (value !== undefined && value !== null) {
                              acc[key] = String(value)
                          }
                          return acc
                      },
                      {} as Record<string, string>
                  )
              ).toString()
            : ''

        return this.get<PaginatedResponse<Winemaker>>(
            `/winemakers${queryString ? `?${queryString}` : ''}`
        )
    }

    async getWinemaker(id: number): Promise<ApiResponse<Winemaker>> {
        return this.get<Winemaker>(`/winemakers/${id}`)
    }

    async getWinemakerWines(
        id: number,
        params?: WineListParams
    ): Promise<ApiResponse<WineListResponse>> {
        const queryString = params
            ? new URLSearchParams(
                  Object.entries(params).reduce(
                      (acc, [key, value]) => {
                          if (value !== undefined && value !== null) {
                              acc[key] = String(value)
                          }
                          return acc
                      },
                      {} as Record<string, string>
                  )
              ).toString()
            : ''

        return this.get<WineListResponse>(
            `/winemakers/${id}/wines${queryString ? `?${queryString}` : ''}`
        )
    }

    // Scan methods
    async scanWine(image: File): Promise<ApiResponse<ScanResponse>> {
        const formData = new FormData()
        formData.append('image', image)
        return this.upload<ScanResponse>('/scan', formData)
    }

    async getScans(params?: {
        page?: number
        limit?: number
    }): Promise<ApiResponse<ScannedWinesResponse>> {
        const queryString = params
            ? new URLSearchParams(
                  Object.entries(params).reduce(
                      (acc, [key, value]) => {
                          if (value !== undefined && value !== null) {
                              acc[key] = String(value)
                          }
                          return acc
                      },
                      {} as Record<string, string>
                  )
              ).toString()
            : ''

        return this.get<ScannedWinesResponse>(`/scans${queryString ? `?${queryString}` : ''}`)
    }

    async createScan(bottleId: string): Promise<ApiResponse<Wine>> {
        return this.post<Wine>('/scans', { bottle_id: bottleId })
    }

    async getScan(scanId: string): Promise<ApiResponse<ScannedWine>> {
        return this.get<ScannedWine>(`/scans/${scanId}`)
    }

    async recognizeWine(imageFile: File): Promise<ApiResponse<ScannedWine>> {
        const formData = new FormData()
        formData.append('image', imageFile)
        return this.upload<ScannedWine>('/scans/recognize', formData)
    }

    // Image methods
    async getImage(id: number): Promise<ApiResponse<Image>> {
        return this.get<Image>(`/images/${id}`)
    }

    async uploadImage(image: File, role: string, alt?: string): Promise<ApiResponse<Image>> {
        const formData = new FormData()
        formData.append('image', image)
        formData.append('role', role)
        if (alt) formData.append('alt', alt)
        return this.upload<Image>('/images', formData)
    }
}

export const apiService = new ApiService()
